import { AppToaster } from '@oy/uidesktop';
import { useCacheProvider } from '@piotr-cz/swr-idb-cache';
import numeral from 'numeral';
import { useEffect } from 'react';
import { MainLoader } from 'src/components';
import { useDomainEnv, useTranslate } from 'src/hooks';
import { StorageKey, storage } from 'src/utilities/Storage';
import { SWRConfig } from 'swr';
import Router from './Router';
import { swrMiddleware } from './utilities/swrConfig';

/**
 * This file represents the main component of the application.
 * It serves as the entry point for rendering the entire application.
 * The component is responsible for defining the routes and rendering the appropriate components based on the current URL.
 * It also handles authentication and authorization by using conditional statements to specify who can access each page.
 */
const App = () => {
  const { isDomainProdEnv } = useDomainEnv();
  const { lang } = useTranslate();

  numeral.locale(`${lang ?? 'id'}-idr`);

  useEffect(() => {
    // This will run when the page first loads and whenever the domain changes
    const defaultTitle = import.meta.env.VITE_DEFAULT_TITLE;
    document.title = isDomainProdEnv ? defaultTitle : `[DEV] ${defaultTitle}`;
  }, [isDomainProdEnv]);

  /**
   * This logic is used by feature announcement page
   *  to go to staging url from production.
   */
  const visitFeaturePathname = storage.get(
    StorageKey.VISIT_FEATURE_URL_PATHNAME_KEY,
    '',
  );
  if (visitFeaturePathname) {
    storage.remove(StorageKey.VISIT_FEATURE_URL_PATHNAME_KEY);
    window.location.pathname = visitFeaturePathname;
  }

  // Initialize
  const cacheProvider = useCacheProvider({
    dbName: 'oy-desktop',
    storeName: 'swr-cache',
  });

  // Cache Provider is being initialized - render fallback component in the meantime
  if (!cacheProvider) {
    return <MainLoader />;
  }
  return (
    <>
      <SWRConfig value={{ provider: cacheProvider, use: [swrMiddleware] }}>
        <Router />
      </SWRConfig>
      <AppToaster />
    </>
  );
};

export default App;
